import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { selectAuth } from '../../../services/auth/selectors'
import { selectUserLanguagePrimary } from '../../../services/user/index'
import { SeedQuestionFeature } from '../../../graphql-types'
import { useDivinationSeedQuestionListQuery } from '../queries.generated'
import { useMidnightRefresh } from './useMidnightRefresh'
import { extractTagMeta, SeedQuestionTagMeta } from './seedQuestionTagUtils'

/**
 * A seed question enriched with parsed tag metadata.
 */
export interface OracleCardsSeedQuestion {
  id: string
  question: string
  tagMeta: SeedQuestionTagMeta
}

interface UseOracleCardsSeedQuestionsParams {
  /** Whether to skip the query (e.g., not in an oracle cards session) */
  skip?: boolean
}

/**
 * Fetches oracle-cards-specific seed questions using the DivinationSeedQuestionList
 * query. The backend uses the session state to determine which questions to return.
 * Parses tag metadata for actions like 'oracle_pull' and rendering hints like 'isPrimary'.
 *
 * Returns an empty array when `skip` is true or no data is available.
 */
export const useOracleCardsSeedQuestions = ({
  skip = false,
}: UseOracleCardsSeedQuestionsParams) => {
  const auth = useSelector(selectAuth)
  const language = useSelector(selectUserLanguagePrimary)

  const {
    data,
    loading,
    error,
    refetch,
  } = useDivinationSeedQuestionListQuery({
    skip,
    variables: {
      feature: SeedQuestionFeature.OracleCards,
      language,
      limit: 6,
    },
    context: {
      jwt: auth.get('jwt'),
    },
    // Re-fetch when chatSessionId changes (e.g., after card pull completes)
    // The backend uses the session state to determine which seeds to return
    fetchPolicy: 'network-only',
  })

  // Re-fetch seed questions at midnight so the daily card pull option reappears
  useMidnightRefresh(refetch, skip)

  const seedQuestions: OracleCardsSeedQuestion[] = useMemo(() => {
    if (!data?.divinationSeedQuestionList?.seedQuestions) return []

    return data.divinationSeedQuestionList.seedQuestions.map((sq) => ({
      id: sq.id,
      question: sq.question,
      tagMeta: extractTagMeta(sq.tags),
    }))
  }, [data])

  return {
    seedQuestions,
    loading,
    error,
    refetch,
  }
}
